import { normalizedConversationPhone, conversationKey } from "./conversationIdentity.mjs";
import { isIncompatibleReplyReminder } from "./replyReminderInvariant.mjs";
import { hasConfirmedProspectOutbound, hasCurrentProspectReply } from "./prospectCycleEvidence.mjs";

export const COMMERCIAL_STATUS_TO_LEAD_STAGE = Object.freeze({
  em_conversa: "qualificacao",
  qualificado: "qualificado",
  vai_na_loja: "vai_na_loja",
  prospeccao_futura: "prospeccao_futura",
  venda: "fechou_venda",
  desqualificado: "desqualificado",
});

// Presencial (decisão da Ana, 26/09/2026): o Motor só leva até "Mensagem
// enviada". "Em negociação" é sempre marcada à mão pelo consultor.
export const COMMERCIAL_STATUS_TO_CLIENT_STAGE = Object.freeze({
  em_conversa: "waiting",
  qualificado: "msg_sent",
  vai_na_loja: "msg_sent",
  prospeccao_futura: "future",
  venda: "closed",
  desqualificado: "semInteresse",
});

export const COMMERCIAL_STATUS_TO_PROSPECT_STAGE = Object.freeze({
  em_conversa: "respondeu",
  qualificado: "interesse",
  vai_na_loja: "interesse",
  prospeccao_futura: "contatado",
  venda: "venda",
  desqualificado: "sem_interesse",
});

export const COMMERCIAL_PROJECTION_META = "__commercialProjection";

// ── Regra "o Motor nunca faz o card voltar etapa" (Ana, 26/09/2026) ───────
// A etapa salva no card só é trocada pela do Motor quando o Motor AVANÇA,
// quando manda para uma saída, ou quando o cliente em prospecção futura
// volta a escrever depois de o card ter ido para lá (reabertura).
// Venda sempre vale. Sem interesse / desqualificado marcados à mão ficam.
const STAGE_RULES = Object.freeze({
  lead: {
    order: { chegou: 0, qualificacao: 1, followup_programado: 1, qualificado: 2, vai_na_loja: 3 },
    sale: ["fechou_venda"], closed: ["desqualificado"], future: ["prospeccao_futura"],
  },
  client: {
    order: { waiting: 0, msg_sent: 1, final: 2 },
    sale: ["closed", "posVenda", "nextSale"], closed: ["semInteresse"], future: ["future"],
  },
  prospect: {
    order: { contatado: 0, respondeu: 1, interesse: 2 },
    sale: ["venda"], closed: ["sem_interesse", "sem_resposta"], future: [],
  },
});

function savedStageAt(record, stage) {
  const candidates = [
    stage === "future" ? record?.futureStageEnteredAt : null,
    record?.stageDates?.[stage], record?.ultima_atualizacao, record?.updatedAt,
  ];
  for (const value of candidates) {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const t = Date.parse(value || "");
    if (Number.isFinite(t)) return t;
  }
  return NaN;
}

export function motorMayProjectStage(kind, record, savedStage, motorStage, state) {
  const rules = STAGE_RULES[kind];
  if (!rules || !motorStage) return false;
  if (record?.manualStageUpdatedAt && savedStage) return savedStage === motorStage;
  if (!savedStage || savedStage === motorStage) return true;
  if (rules.sale.includes(savedStage)) return false;
  if (rules.sale.includes(motorStage)) return true;
  if (rules.closed.includes(savedStage)) return false;
  if (rules.future.includes(savedStage)) {
    if (!(motorStage in rules.order)) return false;
    const inboundAt = Date.parse(state?.lastInboundAt || "");
    const savedAt = savedStageAt(record, savedStage);
    if (!Number.isFinite(inboundAt)) return false;
    // Datas legadas sem horário não provam a ordem de dois eventos no mesmo dia.
    // A reabertura só é segura no dia seguinte; novos cards salvam o instante exato.
    if (typeof savedAt === "string") return dateOnly(state.lastInboundAt) > savedAt;
    return Number.isFinite(savedAt) && inboundAt > savedAt;
  }
  if (!(savedStage in rules.order)) return true;
  if (!(motorStage in rules.order)) return true;
  return rules.order[motorStage] > rules.order[savedStage];
}

function clone(value) {
  return value && typeof value === "object" ? JSON.parse(JSON.stringify(value)) : value;
}

export function dateOnly(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = type => parts.find(part => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function trackingChannelStore(record) {
  if (!record || typeof record !== "object") return null;
  const value = record.whatsappStoreId ?? record.lojaOrigem ?? record.lojaAtual ?? record.storeId;
  const storeId = Number(value);
  return Number.isInteger(storeId) && storeId > 0 ? storeId : null;
}

export function trackingKey(storeId, phone) {
  return conversationKey(Number(storeId), normalizedConversationPhone(phone));
}

export function canonicalSaleChannelStore(target, fallbackStoreId = null) {
  const value = target?.channelStoreId ?? target?.whatsappStoreId ?? target?.lojaOrigem
    ?? target?.channel?.storeId ?? fallbackStoreId;
  const storeId = Number(value);
  return Number.isInteger(storeId) && storeId > 0 ? storeId : null;
}

export function createSaleTarget(moduleId, contact = {}, fallbackStoreId = null, fallbackConsultantId = null) {
  const storeId = contact.storeId ?? contact.lojaAtual ?? fallbackStoreId;
  return {
    moduleId,
    phone: contact.phone ?? contact.telefone ?? "",
    name: contact.name ?? contact.nome ?? "",
    storeId,
    sourceStoreId: contact.sourceStoreId ?? storeId,
    sourceId: contact.sourceId ?? contact.id,
    consultantId: contact.consultantId
      ?? contact.assignedConsultantId
      ?? contact.consultorAtual
      ?? contact.responsibleId
      ?? contact.ownerId
      ?? fallbackConsultantId,
    ...(contact.channelStoreId != null ? { channelStoreId: contact.channelStoreId } : {}),
    ...(contact.whatsappStoreId != null ? { whatsappStoreId: contact.whatsappStoreId } : {}),
    ...(contact.lojaOrigem != null ? { lojaOrigem: contact.lojaOrigem } : {}),
  };
}

export function reconcileCommercialTrackingSnapshot(previous, incoming) {
  const before = normalizeCommercialTrackingSnapshot(previous);
  const next = normalizeCommercialTrackingSnapshot(incoming);
  for (const [key, state] of Object.entries(next)) {
    if (Number(before[key]?.version || 0) > Number(state.version || 0)) next[key] = before[key];
  }
  return next;
}

export function normalizeCommercialTrackingSnapshot(raw, allowedStoreIds = null) {
  const allowed = allowedStoreIds == null
    ? null
    : new Set((Array.isArray(allowedStoreIds) ? allowedStoreIds : [allowedStoreIds])
      .map(Number).filter(id => Number.isInteger(id) && id > 0));
  const index = {};
  const add = (storeId, phone, value) => {
    const key = trackingKey(storeId, phone);
    if (!key || !value || typeof value !== "object") return;
    const numericStore = Number(storeId);
    if (allowed && !allowed.has(numericStore)) return;
    if (Number(value.storeId) !== numericStore) return;
    const normalizedPhone = normalizedConversationPhone(value.phone || phone);
    if (!normalizedPhone || normalizedPhone !== normalizedConversationPhone(phone)) return;
    if (Number(index[key]?.version || 0) > Number(value.version || 0)) return;
    index[key] = { ...clone(value), storeId: numericStore, phone: normalizedPhone, conversationId: key };
  };
  if (!raw || typeof raw !== "object") return index;
  Object.entries(raw).forEach(([storeId, phones]) => {
    if (!phones || typeof phones !== "object") return;
    const flatKey = String(storeId).match(/^(\d+):(\d+)$/);
    if (flatKey) { add(flatKey[1],flatKey[2],phones); return; }
    Object.entries(phones).forEach(([phone, value]) => add(storeId, phone, value));
  });
  return index;
}

function stateFor(record, index) {
  const key = trackingKey(trackingChannelStore(record), record?.telefone || record?.phone);
  return key ? index?.[key] || null : null;
}

function isPersistedDisqualifiedLead(record) {
  return record?.desqualificado === true
    || record?.estagio === "desqualificado"
    || record?._crm_estagio === "desqualificado";
}

function projected(record, state, mapping, dateField) {
  if (!state || !mapping[state.commercialStatus]) return clone(record);
  const raw = clone(record);
  const projectedDate = dateOnly(state.nextActionAt) || null;
  const fields = {
    [dateField]: {
      rawPresent: Object.prototype.hasOwnProperty.call(raw || {}, dateField),
      rawValue: clone(raw?.[dateField]),
      projectedValue: projectedDate,
    },
  };
  return {
    ...raw,
    [dateField]: projectedDate,
    [COMMERCIAL_PROJECTION_META]: { fields },
  };
}

export function withStageProjection(record, field, projectedValue) {
  const raw = clone(record);
  const metadata = raw?.[COMMERCIAL_PROJECTION_META] || { fields: {} };
  const previous = metadata.fields[field];
  const stillProjected = previous && projectionValueEqual(raw?.[field], previous.projectedValue);
  metadata.fields[field] = {
    rawPresent: stillProjected ? previous.rawPresent : Object.prototype.hasOwnProperty.call(raw || {}, field),
    rawValue: stillProjected ? previous.rawValue : clone(raw?.[field]),
    projectedValue,
  };
  return { ...raw, [field]: projectedValue, [COMMERCIAL_PROJECTION_META]: metadata };
}

function projectionValueEqual(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function sanitizeProjectedRecord(outgoing, currentRaw = {}) {
  const source = outgoing && typeof outgoing === "object" ? outgoing : {};
  const metadata = source[COMMERCIAL_PROJECTION_META];
  if (!metadata?.fields || typeof metadata.fields !== "object") return clone(source);
  const result = { ...clone(source) };
  delete result[COMMERCIAL_PROJECTION_META];
  Object.entries(metadata.fields).forEach(([field, descriptor]) => {
    if (!projectionValueEqual(result[field], descriptor.projectedValue)) return;
    if (descriptor.rawPresent) result[field] = clone(descriptor.rawValue);
    else delete result[field];
  });
  return result;
}

function sameRecord(left, right) {
  if (!left || !right) return false;
  if (left.id != null && right.id != null) return String(left.id) === String(right.id);
  const leftPhone = normalizedConversationPhone(left.telefone || left.phone);
  const rightPhone = normalizedConversationPhone(right.telefone || right.phone);
  return Boolean(leftPhone && rightPhone && leftPhone === rightPhone
    && trackingChannelStore(left) === trackingChannelStore(right));
}

export function sanitizeProjectedRecords(outgoingRecords, currentRawRecords = []) {
  const raw = Array.isArray(currentRawRecords) ? currentRawRecords : [];
  return (Array.isArray(outgoingRecords) ? outgoingRecords : [])
    .map(record => sanitizeProjectedRecord(
      record,
      raw.find(candidate => sameRecord(record, candidate)) || {},
    ));
}

export function projectLead(record, index) {
  // A manual, persisted disqualification is authoritative even if the
  // commercial tracking snapshot still says the conversation is active.
  if (isPersistedDisqualifiedLead(record)) return { ...clone(record), estagio:"desqualificado" };
  const state = stateFor(record, index);
  const stage = state && COMMERCIAL_STATUS_TO_LEAD_STAGE[state.commercialStatus];
  // A etapa salva pelo consultor só cede ao Motor quando ele avança, manda
  // para uma saída ou reabre uma prospecção futura (motorMayProjectStage).
  // Assim o card não "volta" a cada atualização.
  const savedLeadStage = record?._crm_estagio || record?.estagio;
  if (stage && !motorMayProjectStage("lead", record, savedLeadStage, stage, state)) {
    return clone(record);
  }
  // An older Motor cancellation must not erase a next-contact date saved to
  // the lead afterwards. Without the date, the conversation exit guard would
  // trap the consultant even though the lead already has a confirmed schedule.
  const leadUpdatedAt = Date.parse(record?.ultima_atualizacao || "");
  const appointmentUpdatedAt = Date.parse(record?.followUpUpdatedAt || "");
  const trackingUpdatedAt = Date.parse(state?.updatedAt || state?.lastEventAt || "");
  const leadDateSuperseded = Boolean(dateOnly(state?.statusChangedAt) && dateOnly(record?.proxima_data_contato)
    && dateOnly(record.proxima_data_contato) <= dateOnly(state.statusChangedAt));
  const explicitNewerAppointment = Number.isFinite(appointmentUpdatedAt)
    && Number.isFinite(trackingUpdatedAt) && appointmentUpdatedAt > trackingUpdatedAt;
  const newerManualDate = Boolean(dateOnly(record?.proxima_data_contato))
    && (Boolean(record?.followUpUpdatedAt) || explicitNewerAppointment || (
      !leadDateSuperseded && !dateOnly(state?.nextActionAt)
      && Number.isFinite(leadUpdatedAt) && Number.isFinite(trackingUpdatedAt)
      && leadUpdatedAt > trackingUpdatedAt
    ));
  const result = newerManualDate ? clone(record)
    : projected(record, state, COMMERCIAL_STATUS_TO_LEAD_STAGE, "proxima_data_contato");
  if (stage === "desqualificado" && state?.autoDisqualification && !result.motivo_desqualificacao_tipo) {
    result.motivo_desqualificacao_tipo = "sem_retorno";
    result.motivo_desqualificacao = result.motivo_desqualificacao || "Sem retorno (fim do follow-up automático)";
  }
  if (!stage) return result;
  return withStageProjection(
    withStageProjection(result, "estagio", stage),
    "_crm_estagio",
    stage,
  );
}

// Etapas finais do Atendimento Presencial gravadas no próprio card (venda
// registrada, pós-venda, próxima venda, sem interesse). O snapshot do Motor
// pode estar atrasado (evento de venda rejeitado/atrasado) e não pode fazer o
// card voltar para "Em acompanhamento" — mesma regra que projectLead aplica à
// desqualificação persistida.
const CLIENT_PERSISTED_TERMINAL_STAGES = new Set(["closed", "posVenda", "nextSale", "semInteresse"]);

export function projectClient(record, index) {
  const state = stateFor(record, index);
  let stage = state && COMMERCIAL_STATUS_TO_CLIENT_STAGE[state.commercialStatus];
  // Presencial: a primeira mensagem enviada pelo WhatsApp já leva o cliente
  // de "Em acompanhamento" para "Mensagem enviada", mesmo sem resposta.
  // Após reabrir uma Prospecção futura, uma mensagem do ciclo anterior não conta.
  const reopenedFuture = record?.stage === "future" && stage === "waiting";
  const freshOutbound = !reopenedFuture
    || (Number.isFinite(Date.parse(state?.lastInboundAt || ""))
      && Date.parse(state?.lastOutboundAt || "") > Date.parse(state.lastInboundAt));
  if (stage === "waiting" && state?.lastOutboundAt && freshOutbound) stage = "msg_sent";
  if (CLIENT_PERSISTED_TERMINAL_STAGES.has(record?.stage)
    && (!stage || !CLIENT_PERSISTED_TERMINAL_STAGES.has(stage))) {
    return clone(record);
  }
  if (stage && !motorMayProjectStage("client", record, record?.stage, stage, state)) return clone(record);
  const result = record.followUpUpdatedAt && dateOnly(record.followUp) ? clone(record)
    : projected(record, state, COMMERCIAL_STATUS_TO_CLIENT_STAGE, "followUp");
  return stage ? withStageProjection(result, "stage", stage) : result;
}

export function projectProspect(record, index) {
  const state = stateFor(record, index);
  // The prospect-only, one-contact window is independent from the Motor's
  // legacy progressive follow-up dates. Never resurrect a cleaned return.
  if (record?.stage === "sem_resposta" && record?.responseWindow?.version === 1
    && record.responseWindow.status === "closed") {
    const result = clone(record);
    delete result.followUp;
    delete result.nextContactDate;
    delete result.proxima_data_contato;
    result.nextContactStatus = "completed";
    if (state?.commercialStatus === "venda") return { ...result, stage:"venda" };
    if (hasCurrentProspectReply(state)
      && Date.parse(state.prospectCycle.replyAt) > Date.parse(record.responseWindow.lastOutboundAt || "")) {
      return { ...result, stage:"respondeu" };
    }
    return result;
  }
  let stage = null;
  if (state) {
    const status = state.commercialStatus;
    if (["venda", "desqualificado"].includes(status)) {
      stage = COMMERCIAL_STATUS_TO_PROSPECT_STAGE[status];
    } else if (status === "prospeccao_futura" && hasConfirmedProspectOutbound(state)) {
      stage = "contatado";
    } else if (state.manualCommercialStatus === status && status !== "prospeccao_futura"
      && COMMERCIAL_STATUS_TO_PROSPECT_STAGE[status]) {
      // An explicit Motor override remains an intentional stage decision.
      stage = COMMERCIAL_STATUS_TO_PROSPECT_STAGE[status];
    } else if (hasCurrentProspectReply(state)) {
      stage = ["qualificado", "vai_na_loja"].includes(status) ? "interesse" : "respondeu";
    } else if (hasConfirmedProspectOutbound(state)) {
      stage = "contatado";
    }
  }
  if (stage && !motorMayProjectStage("prospect", record, record?.stage, stage, state)) return clone(record);
  // A manual follow-up confirmed after the Motor's last update is newer
  // evidence. Do not replace it with an older projected date (or null).
  const manualUpdatedAt = Date.parse(record?.followUpUpdatedAt || "");
  const trackingUpdatedAt = Date.parse(state?.updatedAt || state?.lastEventAt || "");
  const newerManualDate = Boolean(dateOnly(record?.followUp))
    && (Boolean(record?.followUpUpdatedAt)
      || (Number.isFinite(manualUpdatedAt) && Number.isFinite(trackingUpdatedAt)
        && manualUpdatedAt > trackingUpdatedAt));
  const result = newerManualDate ? clone(record)
    : projected(record, state, COMMERCIAL_STATUS_TO_PROSPECT_STAGE, "followUp");
  if ((stage || result.stage) === "contatado") {
    const waiting = stage ? withStageProjection(result, "stage", stage) : { ...result };
    waiting.nextContactStatus = "completed";
    const fields = { ...waiting[COMMERCIAL_PROJECTION_META]?.fields };
    for (const field of ["followUp", "nextContactDate", "proxima_data_contato", "nextContactStatus"]) {
      fields[field] = {
        rawPresent:Object.prototype.hasOwnProperty.call(record, field),
        rawValue:clone(record[field]),
        projectedValue:field === "nextContactStatus" ? "completed" : undefined,
      };
      if (field !== "nextContactStatus") delete waiting[field];
    }
    waiting[COMMERCIAL_PROJECTION_META] = { fields };
    return waiting;
  }
  if (!stage) {
    if (!state) return result;
    return withStageProjection(
      result,
      "nextContactDate",
      newerManualDate ? dateOnly(record.followUp) : dateOnly(state.nextActionAt) || null,
    );
  }
  const withStage = withStageProjection(result, "stage", stage);
  return withStageProjection(
    withStage,
    "nextContactDate",
    newerManualDate ? dateOnly(record.followUp) : dateOnly(state.nextActionAt) || null,
  );
}

export function projectLeads(records, index) {
  return (Array.isArray(records) ? records : []).map(record => projectLead(record, index));
}

export function projectClients(records, index) {
  return (Array.isArray(records) ? records : []).map(record => projectClient(record, index));
}

export function projectProspects(records, index) {
  return (Array.isArray(records) ? records : []).map(record => projectProspect(record, index));
}

// A modal may keep the object that was selected before a tracking update. Resolve
// it against the latest projected collection so confirmed Motor projections reach
// the open conversation without writing anything back to the legacy record.
export function resolveProjectedRecord(records, selected) {
  if (!selected || !Array.isArray(records)) return selected;
  return records.find(record => String(record?.id) === String(selected?.id)) || selected;
}

function commercialReminderId(state) {
  return `commercial:${state.conversationId}:${state.updatedByEventId || state.version}`;
}

function reminderActionFamily(reminder) {
  const action = String(reminder?.nextActionType || reminder?.actionType || reminder?.type || "")
    .trim().toLowerCase();
  if (["followup", "prospeccao_followup", "retomar_contato", "retomar_negociacao"].includes(action)) return "retomar";
  return action;
}

function commercialReminderScopeKey(reminder, resolvedContact = null) {
  const storeId = trackingChannelStore(reminder) ?? trackingChannelStore(resolvedContact);
  const phone = normalizedConversationPhone(reminder?.phone || reminder?.telefone
    || resolvedContact?.phone || resolvedContact?.telefone);
  const action = reminderActionFamily(reminder);
  return storeId && phone && action ? `${storeId}:${phone}:${action}` : null;
}

export function commercialReminderSemanticKey(reminder, resolvedContact = null) {
  const storeId = trackingChannelStore(reminder) ?? trackingChannelStore(resolvedContact);
  const phone = normalizedConversationPhone(reminder?.phone || reminder?.telefone
    || resolvedContact?.phone || resolvedContact?.telefone);
  const action = reminderActionFamily(reminder);
  const dueDate = dateOnly(reminder?.dueDate || reminder?.nextActionAt);
  return storeId && phone && action && dueDate
    ? `${storeId}:${phone}:${action}:${dueDate}`
    : null;
}

export function commercialReminderOverrideEvent(reminder, occurredAt = new Date().toISOString()) {
  const semanticKey = commercialReminderSemanticKey(reminder);
  return {
    type: "manual_override",
    storeId: Number(reminder?.storeId),
    phone: normalizedConversationPhone(reminder?.phone || reminder?.telefone),
    sourceId: `commercial-reminder:${semanticKey || reminder?.id}:${reminder?.id}:complete`,
    occurredAt,
    expectedNextActionDate:dateOnly(reminder?.dueDate || reminder?.nextActionAt),
    nextActionType: null,
    nextActionAt: null,
    nextActionReason: "lembrete_comercial_concluido",
  };
}

function terminalReminderContact(contact) {
  const stage = String(contact.stage ?? contact._crm_estagio ?? contact.estagio ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase().replace(/[^a-z]/g,"");
  return ["seminteresse","desqualificado","fechouvenda","venda","closed"].includes(stage);
}

export function virtualReminderForTracking(state, contact = {}) {
  if (!state || !state.nextActionType || !state.nextActionAt
    || terminalReminderContact(contact)
    || ["venda", "desqualificado"].includes(state.commercialStatus)) return null;
  const id = commercialReminderId(state);
  return {
    id,
    deterministicId: id,
    source: "commercial_tracking",
    ...(contact._reminderField && contact.id ? { [contact._reminderField]:contact.id } : {}),
    type: "commercial",
    status: "pending",
    storeId: Number(state.storeId),
    phone: state.phone,
    nextActionType: state.nextActionType,
    nextActionAt: state.nextActionAt,
    consultantId: state.responsibleId || contact.consultantId || contact.consultorAtual
      || contact.assignedConsultantId || null,
    clientName: contact.name || contact.nome || contact.clientName || state.phone,
    text: `📅 ${state.nextActionType === "retomar_negociacao" ? "Retomar negociação" : state.nextActionType}`,
    dueDate: dateOnly(state.nextActionAt),
    createdAt: state.updatedAt || null,
    doneAt: null,
  };
}

export function virtualReplyReminderForTracking(state, contact = {}) {
  if (state?.awaitingParty !== "awaiting_consultant" || !state.lastInboundAt
    || terminalReminderContact(contact)
    || (state.nextActionReason === "agendado_pelo_consultor" && !state.nextActionAt && !state.nextActionType)
    || ["venda", "desqualificado"].includes(state.commercialStatus)) return null;
  const dueDate = dateOnly(state.lastInboundAt);
  const consultantId = state.responsibleId || contact.consultantId || contact.consultorAtual
    || contact.assignedConsultantId || null;
  if (!dueDate || !consultantId) return null;
  return {
    id:`commercial-reply:${state.conversationId}:${state.lastInboundAt}`,
    ...(contact._reminderField && contact.id ? { [contact._reminderField]:contact.id } : {}),
    source:"commercial_tracking",
    type:"consultant_reply_required",
    status:"pending",
    storeId:Number(state.storeId),
    phone:state.phone,
    consultantId,
    clientName:contact.name || contact.nome || state.phone,
    text:"Responder cliente",
    dueDate,
    createdAt:state.lastInboundAt,
  };
}

export function mergeVirtualCommercialReminders(existing, trackingIndex, contacts = []) {
  const contactList = Array.isArray(contacts) ? contacts : [];
  const disqualifiedLeads = contactList.filter(contact =>
    contact?.telefone && isPersistedDisqualifiedLead(contact));
  const disqualifiedKeys = new Set(disqualifiedLeads.map(contact =>
    trackingKey(trackingChannelStore(contact), contact.telefone)).filter(Boolean));
  const contactsByKey = new Map();
  const contactsByName = new Map();
  const contactsByStoreAndName = new Map();
  contactList.forEach(contact => {
    contactsByKey.set(
      trackingKey(trackingChannelStore(contact), contact?.telefone || contact?.phone),
      contact,
    );
    if (!normalizedConversationPhone(contact?.phone || contact?.telefone)) return;
    const name = String(contact?.name || contact?.nome || contact?.clientName || "").trim().toLowerCase();
    const storeId = trackingChannelStore(contact);
    const byName = contactsByName.get(name) || [];
    byName.push(contact);
    contactsByName.set(name, byName);
    const storeAndName = `${storeId}:${name}`;
    const byStoreAndName = contactsByStoreAndName.get(storeAndName) || [];
    byStoreAndName.push(contact);
    contactsByStoreAndName.set(storeAndName, byStoreAndName);
  });
  const contactMatches = (reminder) => {
    const field = ["clientId", "leadId", "prospId"].find(key => reminder?.[key] != null);
    if (field) {
      const norm = value => field === "leadId" ? String(value).replace(/^aurora_/, "") : String(value);
      const exact = contactList.filter(contact => contact._reminderField === field
        && norm(contact.id) === norm(reminder[field]));
      return exact.length === 1 ? exact[0] : null;
    }
    if (reminder?.phone || reminder?.telefone || !reminder?.clientName) return null;
    const storeId = trackingChannelStore(reminder);
    const name = String(reminder.clientName).trim().toLowerCase();
    const matches = storeId == null
      ? contactsByName.get(name) || []
      : contactsByStoreAndName.get(`${storeId}:${name}`) || [];
    // Duplicate lead projections may describe the same conversation. Resolve
    // only if every match has the same tenant-qualified phone; identical names
    // with different phones remain ambiguous.
    const identities = new Set(matches.map(contact =>
      trackingKey(trackingChannelStore(contact), contact.phone || contact.telefone)));
    return identities.size === 1 ? matches[0] : null;
  };
  const belongsToDisqualifiedLead = reminder => {
    const id = String(reminder?.leadId || "").replace(/^aurora_/, "");
    const ownerStore = Number(reminder?.storeId || reminder?.responsibleStoreId) || null;
    if (id && disqualifiedLeads.some(contact => {
      const ids = [contact.id, contact._rawPhone]
        .filter(Boolean).map(value => String(value).replace(/^aurora_/, ""));
      return ids.includes(id) && (ownerStore
        ? ownerStore === Number(contact.storeId || contact.lojaAtual)
        : /^\d+_/.test(id));
    })) return true;
    const matched = reminder?.phone || reminder?.telefone ? reminder : contactMatches(reminder);
    const key = trackingKey(trackingChannelStore(matched),
      matched?.phone || matched?.telefone);
    return Boolean(key && disqualifiedKeys.has(key));
  };
  const trackingReminders = Object.values(trackingIndex || {}).map(state => {
    const contact = contactsByKey.get(trackingKey(state.storeId, state.phone));
    const disqualified = disqualifiedKeys.has(trackingKey(state.storeId, state.phone));
    const reminder = disqualified ? null : virtualReminderForTracking(state, contact);
    const adoptedScopeKey = commercialReminderScopeKey({
      storeId:state.storeId,
      phone:state.phone,
      nextActionType:"retomar_negociacao",
    }, contact);
    const semanticKey = reminder ? commercialReminderSemanticKey(reminder, contact) : null;
    return { reminder, adoptedScopeKey, semanticKey };
  });
  const semanticKeyByScope = new Map(
    trackingReminders.map(({ adoptedScopeKey, semanticKey }) => [adoptedScopeKey, semanticKey]),
  );
  const result = (Array.isArray(existing) ? existing : []).filter(item => {
    if (item?.status !== "pending") return true;
    if (belongsToDisqualifiedLead(item)) return false;
    if (isIncompatibleReplyReminder(item, trackingIndex)) {
      console.warn("[commercial-tracking] resposta pendente incompatível com aguardando cliente", item.id);
      return false;
    }
    const itemContact = contactMatches(item);
    const itemScopeKey = commercialReminderScopeKey(item, itemContact);
    if (!semanticKeyByScope.has(itemScopeKey)) return true;
    const semanticKey = semanticKeyByScope.get(itemScopeKey);
    return Boolean(semanticKey)
      && commercialReminderSemanticKey(item, itemContact) === semanticKey;
  }).map(item => ({ ...item }));
  const existingIds = new Set(result.map(item => String(item?.id ?? item?.deterministicId ?? "")));
  const existingSemanticKeys = new Set(
    result.map(item => commercialReminderSemanticKey(item, contactMatches(item))).filter(Boolean),
  );
  trackingReminders.forEach(({ reminder, semanticKey }) => {
    if (!reminder) return;
    if (existingIds.has(reminder.id)) return;
    if (semanticKey && existingSemanticKeys.has(semanticKey)) return;
    result.push(reminder);
    existingIds.add(reminder.id);
    if (semanticKey) existingSemanticKeys.add(semanticKey);
  });
  Object.values(trackingIndex || {}).forEach(state => {
    if (disqualifiedKeys.has(trackingKey(state.storeId, state.phone))) return;
    const contact = contactsByKey.get(trackingKey(state.storeId, state.phone));
    const reminder = virtualReplyReminderForTracking(state, contact);
    if (!reminder) return;
    if (result.some(item => item.status === "pending"
      && item.type === "consultant_reply_required"
      && commercialReminderScopeKey(item, contactMatches(item)) === commercialReminderScopeKey(reminder))) return;
    result.push(reminder);
  });
  return result;
}
